import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';
import { AccessDeniedState, ErrorState, PageLoader } from '../../components/states';
import { api, getErrorMessage } from '../../lib/api';
import type { SubscriptionDetail } from '../../lib/schemas';
import { useAuth, type PortalUser } from './AuthContext';

export function isSubscriptionOwner(
  user: Pick<PortalUser, 'uid'> | null,
  subscription: Pick<SubscriptionDetail, 'customerUid'>,
): boolean {
  return Boolean(user && user.uid === subscription.customerUid);
}

export function CustomerGuard() {
  const { user, loading, error } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader label="Güvenli oturumunuz doğrulanıyor…" />;
  if (error) return <ErrorState message={error} title="Oturum doğrulanamadı" />;
  if (!user) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate
        replace
        to={`/auth/exchange?reason=signin&returnTo=${encodeURIComponent(returnTo)}`}
      />
    );
  }
  return <Outlet />;
}

export function AdminGuard() {
  const { user, loading, error } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader label="Yönetici yetkisi doğrulanıyor…" />;
  if (error) return <ErrorState message={error} title="Oturum doğrulanamadı" />;
  if (!user) {
    return (
      <Navigate replace to={`/admin/login?returnTo=${encodeURIComponent(location.pathname)}`} />
    );
  }
  if (!user.isAdmin) return <AccessDeniedState admin />;
  return <Outlet />;
}

export interface SubscriptionOutletContext {
  subscription: SubscriptionDetail;
}

export function SubscriptionOwnershipGuard() {
  const { subscriptionId } = useParams();
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['subscription', subscriptionId],
    queryFn: ({ signal }) => {
      if (!subscriptionId) throw new Error('Abonelik kimliği eksik.');
      return api.getSubscription(subscriptionId, signal);
    },
    enabled: Boolean(subscriptionId && user),
    retry: false,
  });

  if (query.isPending) return <PageLoader label="Aboneliğiniz açılıyor…" />;
  if (query.isError) {
    return (
      <ErrorState
        message={getErrorMessage(query.error)}
        onRetry={() => void query.refetch()}
        title="Abonelik alınamadı"
      />
    );
  }
  if (!isSubscriptionOwner(user, query.data)) return <AccessDeniedState />;
  return <Outlet context={{ subscription: query.data } satisfies SubscriptionOutletContext} />;
}
